import { useCallback, useEffect, useState } from 'react';
import { Button, Icon } from '@mercator-gcp/grout';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'components/elements/Spinner';
import SaveCompanyToGroupModal from 'components/widgets/CompanyDetails/SaveCompanyToGroupModal';
import FlyoutMenu from 'components/widgets/Dashboard/FlyoutMenu';
import Section from 'components/widgets/Dashboard/FlyoutMenu/Section';
import SectionItem from 'components/widgets/Dashboard/FlyoutMenu/SectionItem';
import SectionItemList from 'components/widgets/Dashboard/FlyoutMenu/SectionItemList';
import GroupPopoverMenu from 'components/widgets/Dashboard/GroupPopoverMenu';
import { useInvalidateGroupConfigurations } from 'hooks/useGroupConfig';
import { trackGroupConfigAction } from 'services/analytics/trackGroupConfigAction';
import { GroupConfigMinimal } from 'services/API/GroupConfigurationAPI';
import { useUser } from 'state/auth';
import {
  useDashboard,
  useDashboardGroupId,
  useDashboardGroupName,
} from 'state/dashboard';
import { useGroupIdQuery } from 'state/queryParams';
import { NEW_GROUP_ID, GROUP_TYPE } from 'utils/constants';
import { buildDashboardURLWithParams } from 'utils/navigation';

type Props = {
  groupList?: GroupConfigMinimal[] | null;
  isLoading: boolean;
  isFetching: boolean;
  groupType: GROUP_TYPE.PROJECT | GROUP_TYPE.COMPANY;
  onSelectGroupId: (arg0: string) => void;
  onNewSearchClick?: () => void; // Callback to open wizard for new search (for PROJECT type)
};

const GroupConfigMenu = ({
  groupList,
  isLoading,
  isFetching,
  groupType,
  onSelectGroupId,
  onNewSearchClick,
}: Props) => {
  const { groupIdQuery, setGroupIdQuery } = useGroupIdQuery();
  const { startNewProject } = useDashboard();
  const [, setGroupId] = useDashboardGroupId();
  const [, setGroupName] = useDashboardGroupName();
  const navigate = useNavigate();
  const { user } = useUser();
  const { invalidateGroupConfigurations } = useInvalidateGroupConfigurations();
  const [isSaveCompanyModalOpen, setIsSaveCompanyModalOpen] = useState(false);

  const selectGroup = useCallback(
    (id: number | undefined | string, name: string) => {
      setGroupIdQuery?.(String(id));
      setGroupName?.(name);
      setGroupId?.(String(id));
      onSelectGroupId(String(id));
    },
    [setGroupIdQuery, setGroupName, setGroupId, onSelectGroupId],
  );

  const onClickGroup = useCallback(
    (id: number | undefined, name: string) => {
      selectGroup(id, name);
      // When user clicks a company group in the list, show that group and cancel any import flow
      if (groupType === GROUP_TYPE.COMPANY && id != null) {
        navigate(
          buildDashboardURLWithParams({
            groupId: Number(id),
            groupType: GROUP_TYPE.COMPANY,
          }),
          {
            replace: true,
          },
        );
      }
    },
    [selectGroup, groupType, navigate],
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (groupList?.length) {
      // Open group from URL
      if (groupIdQuery) {
        // ensure groupId exists in user's group
        const groupToSelect = groupList.find((item) => item.id === Number(groupIdQuery));
        if (groupToSelect) {
          selectGroup(Number(groupIdQuery), groupToSelect.name);

          return;
        }

        // groupIdQuery is set but not in list yet (e.g. newly created group before refetch) – keep selection
        return;
      }
      // Fallback - Open most recent group
      const mostRecentGroup = groupList[0];
      selectGroup(mostRecentGroup.id, mostRecentGroup.name);
    } else {
      // No groups
      selectGroup('', '');
    }
  }, [groupList, selectGroup, groupIdQuery, isLoading]);

  const isActive = useCallback(
    (id?: number) => id === Number(groupIdQuery),
    [groupIdQuery],
  );

  const onNewGroupClick = useCallback(() => {
    trackGroupConfigAction({
      group_id: '0',
      group_type: groupType === GROUP_TYPE.PROJECT ? 'project_group' : 'company_group',
      click_action: 'start_group',
    });
    if (groupType === GROUP_TYPE.COMPANY) {
      setIsSaveCompanyModalOpen(true);

      return;
    }
    if (groupType === GROUP_TYPE.PROJECT && onNewSearchClick) {
      // Use wizard for project groups if callback provided
      onNewSearchClick();
    } else if (groupType === GROUP_TYPE.PROJECT) {
      // Fallback to navigation if no callback
      navigate(`/saved-search/${NEW_GROUP_ID}/edit`);
    } else {
      if (groupType) {
        startNewProject(groupType);
      }
      navigate(`/dashboard/${NEW_GROUP_ID}`);
    }
  }, [navigate, groupType, startNewProject, onNewSearchClick]);

  return (
    <>
      <FlyoutMenu
        mainAction={
          <Button
            variant="primary"
            onClick={onNewGroupClick}
            startAdornment={<Icon name={Icon.Name.Plus} />}
          >
            {groupType === GROUP_TYPE.COMPANY ? 'New company group' : 'New saved search'}
          </Button>
        }
      >
        <Section title="My saved searches">
          <SectionItemList>
            {isFetching && !isLoading && <Spinner size="sm" />}
            {groupList && !isLoading ? (
              groupList.map(({ id, name, source }) => (
                <SectionItem
                  key={`company-group-menu-${id}`}
                  label={name}
                  isActive={isActive(Number(id))}
                  onClick={() => onClickGroup(id && id, name)}
                  showAiMarker={source === 'scoutbot'}
                  menu={
                    <GroupPopoverMenu
                      groupId={Number(id)}
                      isCompanyGroup={groupType === GROUP_TYPE.COMPANY}
                    />
                  }
                />
              ))
            ) : (
              <Spinner size="sm" />
            )}
          </SectionItemList>
        </Section>
      </FlyoutMenu>
      {groupType === GROUP_TYPE.COMPANY && (
        <SaveCompanyToGroupModal
          isOpen={isSaveCompanyModalOpen}
          onClose={() => setIsSaveCompanyModalOpen(false)}
          createOnly
          onSuccess={(result) => {
            setIsSaveCompanyModalOpen(false);
            if (result) {
              invalidateGroupConfigurations(String(user?.id), GROUP_TYPE.COMPANY);
              selectGroup(result.groupId, result.groupName);
              navigate(
                buildDashboardURLWithParams({
                  groupId: Number(result.groupId),
                  groupType: GROUP_TYPE.COMPANY,
                }),
                {
                  replace: true,
                },
              );
            }
          }}
        />
      )}
    </>
  );
};

export default GroupConfigMenu;
